import React, { useState, useEffect } from 'react'

const FilterTools = ({ image, onFilterApply }) => {
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [filteredPreviews, setFilteredPreviews] = useState({})

  const filters = [
    { id: 'none', name: 'Original', apply: (data) => data },
    {
      id: 'grayscale',
      name: 'Grayscale',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3
          data[i] = data[i + 1] = data[i + 2] = avg
        }
        return data
      }
    },
    {
      id: 'sepia',
      name: 'Sepia',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          data[i] = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189)
          data[i + 1] = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168)
          data[i + 2] = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131)
        }
        return data
      }
    },
    {
      id: 'invert',
      name: 'Invert',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = 255 - data[i]
          data[i + 1] = 255 - data[i + 1]
          data[i + 2] = 255 - data[i + 2]
        }
        return data
      }
    },
    {
      id: 'vintage',
      name: 'Vintage',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, data[i] * 1.2)
          data[i + 1] = Math.min(255, data[i + 1] * 1.0)
          data[i + 2] = Math.min(255, data[i + 2] * 0.8)
        }
        return data
      }
    },
    {
      id: 'cool',
      name: 'Cool',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, data[i] * 0.8)
          data[i + 1] = Math.min(255, data[i + 1] * 1.0)
          data[i + 2] = Math.min(255, data[i + 2] * 1.2)
        }
        return data
      }
    },
    {
      id: 'warm',
      name: 'Warm',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, data[i] * 1.2)
          data[i + 1] = Math.min(255, data[i + 1] * 1.05)
          data[i + 2] = Math.min(255, data[i + 2] * 0.8)
        }
        return data
      }
    },
    {
      id: 'brighten',
      name: 'Brighten',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.min(255, data[i] + 30)
          data[i + 1] = Math.min(255, data[i + 1] + 30)
          data[i + 2] = Math.min(255, data[i + 2] + 30)
        }
        return data
      }
    },
    {
      id: 'darken',
      name: 'Darken',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          data[i] = Math.max(0, data[i] - 30)
          data[i + 1] = Math.max(0, data[i + 1] - 30)
          data[i + 2] = Math.max(0, data[i + 2] - 30)
        }
        return data
      }
    },
    {
      id: 'vibrant',
      name: 'Vibrant',
      apply: (data) => {
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.2989 * data[i] + 0.5870 * data[i + 1] + 0.1140 * data[i + 2]
          const saturation = 1.5
          data[i] = Math.min(255, Math.max(0, gray + (data[i] - gray) * saturation))
          data[i + 1] = Math.min(255, Math.max(0, gray + (data[i + 1] - gray) * saturation))
          data[i + 2] = Math.min(255, Math.max(0, gray + (data[i + 2] - gray) * saturation))
        }
        return data
      }
    }
  ]

  useEffect(() => {
    generatePreviews()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image])

  const generatePreviews = () => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const previews = {}

      filters.forEach(filter => {
        const canvas = document.createElement('canvas')
        canvas.width = 150
        canvas.height = 150
        const ctx = canvas.getContext('2d')

        const aspectRatio = img.width / img.height
        let drawWidth = 150
        let drawHeight = 150

        if (aspectRatio > 1) {
          drawHeight = 150 / aspectRatio
        } else {
          drawWidth = 150 * aspectRatio
        }

        const x = (150 - drawWidth) / 2
        const y = (150 - drawHeight) / 2

        ctx.drawImage(img, x, y, drawWidth, drawHeight)

        if (filter.id !== 'none') {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data
          filter.apply(data)
          ctx.putImageData(imageData, 0, 0)
        }

        previews[filter.id] = canvas.toDataURL()
      })

      setFilteredPreviews(previews)
    }
    img.src = image
  }

  const applyFilter = (filter) => {
    if (filter.id === 'none') {
      onFilterApply(image)
      setSelectedFilter(filter.id)
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      filter.apply(data)
      ctx.putImageData(imageData, 0, 0)

      const filteredImage = canvas.toDataURL()
      onFilterApply(filteredImage)
      setSelectedFilter(filter.id)
    }

    img.src = image
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {filters.map(filter => (
          <div
            key={filter.id}
            className={`cursor-pointer rounded-lg overflow-hidden transition-all ${
              selectedFilter === filter.id
                ? 'ring-4 ring-blue-600 shadow-lg'
                : 'ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-400'
            }`}
            onClick={() => applyFilter(filter)}
          >
            {filteredPreviews[filter.id] && (
              <img
                src={filteredPreviews[filter.id]}
                alt={filter.name}
                className="w-full h-auto"
              />
            )}
            <div className="bg-gray-100 px-2 py-1 text-center">
              <p className="text-xs font-medium text-gray-700">{filter.name}</p>
            </div>
          </div>
        ))}
      </div>

      {selectedFilter && (
        <div className="text-center text-sm text-gray-600">
          Current filter: <span className="font-semibold">
            {filters.find(f => f.id === selectedFilter)?.name}
          </span>
        </div>
      )}
    </div>
  )
}

export default FilterTools
